<template>
  <BaseModal
    v-model="isVisible"
    title="Sign In required"
    :max-width="1200"
    :show-close-button="true"
    @close="handleClose"
    class="login-required-modal"
    :z-index="3000"
  >
    <div class="modal-content">
      <div class="illustration-container">
        <img
          src="/images/signup-popup.svg"
          alt="Sign up illustration"
          class="illustration"
        />
      </div>

      <h3 class="main-title">
        {{ title }}
      </h3>

      <p class="description">
        {{ description }}
      </p>

      <div class="button-container">
        <v-btn
          variant="outlined"
          color="primary"
          size="large"
          class="action-btn login-btn"
          @click="navigateToLogin"
        >
          Sign In
        </v-btn>

        <v-btn
          variant="flat"
          color="primary"
          size="large"
          class="action-btn signup-btn"
          @click="navigateToSignup"
        >
          Sign Up
        </v-btn>
      </div>
    </div>
  </BaseModal>
</template>

<script setup>
import { computed, defineProps, defineEmits } from "vue";
import { useRouter } from "vue-router";
import BaseModal from "./BaseModal.vue";

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  title: {
    type: String,
    default: "Sign In to continue",
  },
  description: {
    type: String,
    default: "Please Sign In or Sign Up to continue generate your Lesson Plan.",
  },
  redirectPath: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(["update:modelValue", "close"]);
const router = useRouter();

const isVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const handleClose = () => {
  emit("close");
};

const navigateToLogin = () => {
  const redirectTo = props.redirectPath || router.currentRoute.value.path;
  router.push({
    path: "/login",
    query: { redirect: redirectTo },
  });
  isVisible.value = false;
};

const navigateToSignup = () => {
  const redirectTo = props.redirectPath || router.currentRoute.value.path;
  router.push({
    path: "/signup",
    query: { redirect: redirectTo },
  });
  isVisible.value = false;
};
</script>

<style scoped>
.login-required-modal :deep(.v-card) {
  border-radius: 20px;
}

.modal-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  padding: 20px 0;
  overflow: hidden;
  min-height: 0;
}

.illustration-container {
  margin-bottom: 10px;
  max-height: min(50vh, 400px);
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.illustration {
  width: 500px;
  height: auto;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.main-title {
  font-size: 40px;
  font-weight: 600;
  color: var(--primary-blue);
  text-align: center;
  margin: 0;
  line-height: 1.2;
}

.description {
  font-size: 18px;
  font-weight: 400;
  color: #3b4648;
  text-align: center;
  margin: 0;
  max-width: 400px;
  line-height: 1.4;
}

.button-container {
  display: flex;
  gap: 20px;
  margin-top: 10px;
}

.action-btn {
  min-width: 120px;
  height: 48px;
  border-radius: 16px;
  font-size: var(--fs-medium);
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
}

.login-btn {
  border-width: 2px;
}

.signup-btn {
  background-color: var(--primary-blue) !important;
}

/* Responsive design */
@media (max-width: 600px) {
  .main-title {
    font-size: var(--fs-xxl);
  }

  .description {
    font-size: var(--fs-medium);
  }

  .button-container {
    flex-direction: column;
    width: 100%;
    gap: 12px;
  }

  .action-btn {
    width: 100%;
  }

  .illustration {
    width: 150px;
  }
}

@media (max-height: 750px) {
  .illustration-container {
    max-height: min(30vh, 200px);
  }

  .illustration {
    width: 250px;
  }

  .modal-content {
    gap: 15px;
    padding: 15px 0;
  }

  .main-title {
    font-size: 32px;
  }

  .description {
    font-size: var(--fs-medium);
  }
}

@media (max-height: 600px) {
  .illustration-container {
    max-height: 120px;
  }

  .illustration {
    width: 250px;
  }

  .modal-content {
    gap: 10px;
    padding: 10px 0;
  }

  .main-title {
    font-size: var(--fs-xxl);
  }

  .description {
    font-size: 15px;
  }
}

@media (max-height: 500px) {
  .illustration-container {
    display: none;
  }

  .modal-content {
    gap: 15px;
    padding: 20px 0;
  }

  .main-title {
    font-size: 26px;
  }

  .description {
    font-size: 15px;
  }
}
</style>
